import React, { useCallback } from 'react';

import {
  Box,
  Radio,
  RadioGroup,
  FormControl,
  FormControlLabel,
  FormLabel,
  Checkbox
} from '@mui/material';

import DebouncedTextField from '../../Utils/debounce';

export default function AccertamentoEliminazione({ parameters, setParameters }) {

  const set = (key, value) =>
    setParameters(prev => ({ ...prev, [key]: value }));

  const setField = useCallback((key, value) => {
    setParameters(prev => ({ ...prev, [key]: value }));
  }, [setParameters]);

  const toggleRadio = (key, newValue) =>
    set(key, parameters[key] === newValue ? '' : newValue);

  const toggleCheckbox = (key, value) => {
    const list = parameters[key] ?? [];

    if (key === 'alterazioniEvacuazione') {
      if (value === 'Nessuna alterazione') {
        set(key, list.includes(value) ? [] : [value]);
        return;
      }

      const nextList = list.includes(value)
        ? list.filter(v => v !== value)
        : [...list.filter(v => v !== 'Nessuna alterazione'), value];

      set(key, nextList);
      return;
    }

    set(key, list.includes(value)
      ? list.filter(v => v !== value)
      : [...list, value]
    );
  };

  const p = parameters;

  return (
    <Box sx={{ width: '50ch', display: 'grid', gap: 2 }}>

      {/* TITOLO */}
      <Box sx={{ p: 3, mb: 2, border: '1px solid rgba(0,0,0,0.23)', borderRadius: 2, backgroundColor: '#f5f5f5', textAlign: 'center' }}>
        <Box component="h1" sx={{ m: 0, fontSize: '1.8rem', fontWeight: 'bold', color: '#1976d2' }}>
          Accertamento Infermieristico
        </Box>

        <Box component="p" sx={{ m: 0, mt: 1, fontSize: '1rem', color: 'text.secondary' }}>
          Modello di eliminazione
        </Box>
      </Box>

      <FormControl sx={{ flexDirection: 'row', gap: 2, alignItems: 'center' }}>
        <FormLabel sx={{ minWidth: 200 }}>Minzione artificiale</FormLabel>

        <RadioGroup row value={p.minzioneArtificiale ?? ''}>
          <FormControlLabel value="true" control={<Radio onClick={() => toggleRadio('minzioneArtificiale', 'true')} />} label="Sì" />
          <FormControlLabel value="false" control={<Radio onClick={() => toggleRadio('minzioneArtificiale', 'false')} />} label="No" />
        </RadioGroup>

        {p.minzioneArtificiale === 'true' && (
          <FormControl sx={{ flexDirection: 'column', gap: 2, alignItems: 'center' }}>
            <RadioGroup row value={p.tipoMinzioneArt ?? ''}>
              <FormControlLabel value="Catetere vescicale" control={<Radio onClick={() => set('tipoMinzioneArt', 'Catetere vescicale')} />} label="Catetere vescicale" />
              <FormControlLabel value="Urostomia" control={<Radio onClick={() => set('tipoMinzioneArt', 'Urostomia')} />} label="Urostomia" />
            </RadioGroup>

            {p.tipoMinzioneArt && (
              <>
                <DebouncedTextField
                  sx={{ minWidth: 220 }}
                  size="small"
                  label="Tipo / Sede"
                  required
                  value={p.sedeMinzioneArt ?? ''}
                  onChange={(val) => setField('sedeMinzioneArt', val)}
                />

                <DebouncedTextField
                  sx={{ minWidth: 245 }}
                  size="small"
                  type="date"
                  label="Inserito il"
                  required
                  InputLabelProps={{ shrink: true }}
                  value={p.dataMinzioneArt ?? ''}
                  onChange={(val) => setField('dataMinzioneArt', val)}
                />
              </>
            )}
          </FormControl>
        )}

        {p.minzioneArtificiale === 'false' && (
          <>
            <DebouncedTextField
              sx={{ maxWidth: 150 }}
              size="small"
              type="number"
              label="Frequenza"
              value={p.frequenzaMinzioni ?? ''}
              onChange={(val) => setField('frequenzaMinzioni', val)}
            />

            <DebouncedTextField
              size="small"
              label="Caratteristiche urine"
              value={p.caratteristicheUrine ?? ''}
              onChange={(val) => setField('caratteristicheUrine', val)}
            />
          </>
        )}
      </FormControl>

      {p.minzioneArtificiale === 'false' && (
        <>
          {[
            ['Disuria', 'disuria'],
            ['Nicturia', 'nicturia'],
            ['Bisogno impellente', 'urgenza'],
            ['Ritenzione', 'ritenzione'],
            ['Difficoltà a rimandare', 'difficoltaRimandare'],
            ['Difficoltà a raggiungere il bagno', 'difficoltaBagno'],
          ].map(([label, key]) => (
            <FormControl key={key} sx={{ flexDirection: 'row', gap: 2, alignItems: 'center' }}>
              <FormLabel sx={{ minWidth: 200 }}>{label}</FormLabel>

              <RadioGroup row value={p[key] ?? ''}>
                <FormControlLabel value="true" control={<Radio onClick={() => toggleRadio(key, 'true')} />} label="Sì" />
                <FormControlLabel value="false" control={<Radio onClick={() => toggleRadio(key, 'false')} />} label="No" />
              </RadioGroup>
            </FormControl>
          ))}

          <FormControl sx={{ flexDirection: 'row', gap: 2, alignItems: 'center' }}>
            <FormLabel sx={{ minWidth: 200 }}>Incontinenza</FormLabel>

            <RadioGroup row value={p.incontinenza ?? ''}>
              <FormControlLabel value="true" control={<Radio onClick={() => toggleRadio('incontinenza', 'true')} />} label="Sì" />
              <FormControlLabel value="false" control={<Radio onClick={() => toggleRadio('incontinenza', 'false')} />} label="No" />
            </RadioGroup>

            {p.incontinenza === 'true' && (
              <FormControl sx={{ flexDirection: 'row', gap: 1, alignItems: 'center' }}>
                {['Diurna', 'Notturna', 'Occasionale', 'Sempre'].map(v => (
                  <FormControlLabel
                    key={v}
                    control={
                      <Checkbox
                        checked={(p.tipoIncontinenza ?? []).includes(v)}
                        onChange={() => toggleCheckbox('tipoIncontinenza', v)}
                      />
                    }
                    label={v}
                  />
                ))}
              </FormControl>
            )}
          </FormControl>

          {p.incontinenza === 'true' && (
            <FormControl sx={{ flexDirection: 'row', gap: 2, alignItems: 'center' }}>
              <FormLabel sx={{ minWidth: 200 }}>Presidi assorbenti</FormLabel>

              <RadioGroup row value={p.presidi ?? ''}>
                <FormControlLabel value="true" control={<Radio onClick={() => toggleRadio('presidi', 'true')} />} label="Sì" />
                <FormControlLabel value="false" control={<Radio onClick={() => toggleRadio('presidi', 'false')} />} label="No" />
              </RadioGroup>
            </FormControl>
          )}
        </>
      )}

      <FormControl sx={{ flexDirection: 'row', gap: 2, alignItems: 'center' }}>
        <FormLabel sx={{ minWidth: 200 }}>Evacuazione artificiale</FormLabel>

        <RadioGroup row value={p.evacuazioneArtificiale ?? ''}>
          <FormControlLabel value="true" control={<Radio onClick={() => toggleRadio('evacuazioneArtificiale', 'true')} />} label="Sì" />
          <FormControlLabel value="false" control={<Radio onClick={() => toggleRadio('evacuazioneArtificiale', 'false')} />} label="No" />
        </RadioGroup>
      </FormControl>

      {p.evacuazioneArtificiale === 'false' && (
        <>
          <FormControl sx={{ flexDirection: 'row', gap: 2, alignItems: 'center' }}>
            <FormLabel sx={{ minWidth: 200 }}>Evacuazione</FormLabel>

            <RadioGroup row value={['stipsi', 'diarrea', 'alvo_eucinetico'].find(k => p[k] === 'true') ?? ''}>
              <FormControlLabel
                value="stipsi"
                control={
                  <Radio
                    onClick={() => {
                      set('stipsi', 'true');
                      set('diarrea', '');
                      set('alvo_eucinetico', '');
                    }}
                  />
                }
                label="Stipsi"
              />

              <FormControlLabel
                value="diarrea"
                control={
                  <Radio
                    onClick={() => {
                      set('diarrea', 'true');
                      set('stipsi', '');
                      set('alvo_eucinetico', '');
                    }}
                  />
                }
                label="Diarrroico"
              />

              <FormControlLabel
                value="alvo_eucinetico"
                control={
                  <Radio
                    onClick={() => {
                      set('alvo_eucinetico', 'true');
                      set('stipsi', '');
                      set('diarrea', '');
                    }}
                  />
                }
                label="Alvo Eucinetico"
              />
            </RadioGroup>

            {p.stipsi === 'true' && (
              <FormControl sx={{ flexDirection: 'row', gap: 2, alignItems: 'center' }}>
                <FormLabel sx={{ minWidth: 200 }}>Uso clisteri/lassativi</FormLabel>

                <RadioGroup row value={p.usoClisteri ?? ''}>
                  <FormControlLabel value="true" control={<Radio onClick={() => toggleRadio('usoClisteri', 'true')} />} label="Sì" />
                  <FormControlLabel value="false" control={<Radio onClick={() => toggleRadio('usoClisteri', 'false')} />} label="No" />
                </RadioGroup>
              </FormControl>
            )}
          </FormControl>

          <FormControl sx={{ flexDirection: 'row', gap: 2, alignItems: 'center' }}>
            <FormLabel>Alterazioni evacuazione</FormLabel>

            {['Nessuna alterazione', 'Rectorragia', 'Ematochezia', 'Melena'].map(v => (
              <FormControlLabel
                key={v}
                control={
                  <Checkbox
                    checked={(p.alterazioniEvacuazione ?? []).includes(v)}
                    onChange={() => toggleCheckbox('alterazioniEvacuazione', v)}
                  />
                }
                label={v}
              />
            ))}
          </FormControl>
        </>
      )}

      {p.evacuazioneArtificiale === 'true' && (
        <FormControl sx={{ flexDirection: 'row', gap: 2, alignItems: 'center' }}>
          <FormLabel sx={{ minWidth: 200 }}>Presenza stomia</FormLabel>

          <RadioGroup row value={p.stomia ?? ''}>
            <FormControlLabel value="true" control={<Radio onClick={() => toggleRadio('stomia', 'true')} />} label="Sì" />
            <FormControlLabel value="false" control={<Radio onClick={() => toggleRadio('stomia', 'false')} />} label="No" />
          </RadioGroup>

          {p.stomia === 'true' && (
            <FormControl sx={{ flexDirection: 'column', gap: 2 }}>
              <DebouncedTextField
                size="small"
                label="Tipo stomia"
                required
                value={p.tipoStomia ?? ''}
                onChange={(val) => setField('tipoStomia', val)}
              />

              <DebouncedTextField
                size="small"
                label="Sede"
                required
                value={p.sedeStomia ?? ''}
                onChange={(val) => setField('sedeStomia', val)}
              />

              <DebouncedTextField
                size="small"
                type="date"
                label="Posizionata il"
                required
                InputLabelProps={{ shrink: true }}
                value={p.dataStomia ?? ''}
                onChange={(val) => setField('dataStomia', val)}
              />
            </FormControl>
          )}
        </FormControl>
      )}
    </Box>
  );
}
